import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getDoctorResume } from "../services/doctorService";
import type { DoctorResume } from "../services/doctorService";
import "./DoctorResumePage.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPhone,
  faEnvelope,
  faLanguage,
  faGraduationCap,
  faUserMd,
  faMapMarkerAlt,
  faVideo,
  faClinicMedical,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";

const DoctorResumePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [resume, setResume] = useState<DoctorResume | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDoctorResume = async () => {
      if (!id) {
        setError("ID du médecin manquant");
        setLoading(false);
        return;
      }

      console.log("Fetching resume for doctor ID:", id);
      setLoading(true);
      setError(null);

      try {
        const data = await getDoctorResume(Number(id));
        console.log("Successfully fetched doctor resume:", data);

        if (!data || !data.profiles || !data.profiles[0]) {
          throw new Error("Données du médecin invalides ou incomplètes");
        }

        setResume(data);
      } catch (err: any) {
        console.error("Error fetching doctor resume:", err);
        setError(
          err.message || "Erreur lors du chargement des informations du médecin"
        );
        setResume(null);
      } finally {
        setLoading(false);
      }
    };

    fetchDoctorResume();
  }, [id]);

  if (loading) {
    return (
      <div className="doctor-resume loading-container">
        <div className="loading">Chargement des informations du médecin...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="doctor-resume error-container">
        <div className="error">
          <h2>Une erreur est survenue</h2>
          <p>{error}</p>
          <button onClick={() => window.history.back()} className="back-button">
            Retour
          </button>
        </div>
      </div>
    );
  }

  if (!resume || !resume.profiles?.[0]) {
    return (
      <div className="doctor-resume error-container">
        <div className="error">
          <h2>Informations non disponibles</h2>
          <p>
            Les informations de ce médecin ne sont pas disponibles pour le
            moment.
          </p>
          <button onClick={() => window.history.back()} className="back-button">
            Retour
          </button>
        </div>
      </div>
    );
  }

  const profile = resume.profiles[0];
  const { doctor } = profile;

  return (
    <div className="doctor-resume">
      <div className="doctor-header">
        <div className="doctor-image">
          <img src={doctor.image} alt={doctor.name} />
        </div>
        <div className="doctor-info">
          <h1>{doctor.name}</h1>
          <h2>{doctor.speciality.label}</h2>
          <p className="location">
            <FontAwesomeIcon icon={faMapMarkerAlt} /> {doctor.city?.name}
          </p>
          <div className="consultation-types">
            {resume.canConsultOnline && (
              <span className="consultation-badge online">
                <FontAwesomeIcon icon={faVideo} /> Consultation en ligne
              </span>
            )}
            {resume.canConsultOffice && (
              <span className="consultation-badge office">
                <FontAwesomeIcon icon={faClinicMedical} /> Consultation au
                cabinet
              </span>
            )}
          </div>
        </div>
      </div>

      {/* <div className="doctor-contact">
        <div className="contact-item">
          <FontAwesomeIcon icon={faPhone} />
          <span>{doctor.phoneNumber}</span>
        </div>
        {doctor.whatsApp && (
          <div className="contact-item">
            <FontAwesomeIcon icon={faWhatsapp} />
            <span>{doctor.whatsApp}</span>
          </div>
        )}
        <div className="contact-item">
          <FontAwesomeIcon icon={faEnvelope} />
          <span>{doctor.email}</span>
        </div>
      </div> */}

      <div className="doctor-sections">
        {profile.presentation && (
          <section className="resume-section">
            <h3>
              <FontAwesomeIcon icon={faUserMd} /> Présentation
            </h3>
            <p>{profile.presentation}</p>
          </section>
        )}

        {profile.formations?.length > 0 && (
          <section className="resume-section">
            <h3>
              <FontAwesomeIcon icon={faGraduationCap} /> Formation
            </h3>
            <ul>
              {profile.formations.map((formation: string, index: number) => (
                <li key={index}>{formation}</li>
              ))}
            </ul>
          </section>
        )}

        {profile.expertises?.length > 0 && (
          <section className="resume-section">
            <h3>
              <FontAwesomeIcon icon={faUserMd} /> Expertises
            </h3>
            <ul>
              {profile.expertises.map((expertise: string, index: number) => (
                <li key={index}>{expertise}</li>
              ))}
            </ul>
          </section>
        )}

        {profile.languages?.length > 0 && (
          <section className="resume-section">
            <h3>
              <FontAwesomeIcon icon={faLanguage} /> Langues
            </h3>
            <ul className="languages-list">
              {profile.languages.map((language) => (
                <li key={language.id}>{language.name}</li>
              ))}
            </ul>
          </section>
        )}

        {profile.association?.length > 0 && (
          <section className="resume-section">
            <h3>Associations</h3>
            <ul>
              {profile.association.map((assoc, index) => (
                <li key={index}>{assoc}</li>
              ))}
            </ul>
          </section>
        )}

        {profile.pubScientifique?.length > 0 && (
          <section className="resume-section">
            <h3>Publications Scientifiques</h3>
            <ul>
              {profile.pubScientifique.map((pub, index) => (
                <li key={index}>{pub}</li>
              ))}
            </ul>
          </section>
        )}
      </div>

      <div className="app-promo-card">
    <div className="circle-decoration circle-1"></div>
    <div className="circle-decoration circle-2"></div>
    <div className="promo-content">
      <div className="promo-text">
        <div className="highlight-tag">Consultation médicale</div>
        <h2>Poursuivez avec ce médecin</h2>
        <p>
          Continuez votre consultation en toute simplicité grâce à notre application. 
          Téléchargez-la maintenant pour un accès immédiat à tous vos services médicaux.
        </p>
        <div className="store-buttons">
          <a 
            href="https://play.google.com/store/apps/details?id=com.yatamedical.YataPatient" 
            target="_blank" 
            rel="noopener noreferrer"
            className="store-button"
          >
            <img src="/images/playStore.png" alt="Disponible sur Google Play" />
          </a>
          <a 
            href="https://apps.apple.com/us/app/yata-m%C3%A9dical/id6615080510" 
            target="_blank" 
            rel="noopener noreferrer"
            className="store-button"
          >
            <img src="/images/googlePlay.png" alt="Télécharger dans l'App Store" />
          </a>
        </div>
      </div>
      <div className="promo-image">
        <img src="/images/app.png" alt="Aperçu de l'application mobile" />
      </div>
    </div>
  </div>
    </div>
  );
};

export default DoctorResumePage;
